// src/lib/managers/conversation/guard.ts

/**
 * Garde du SDK sur les actions groupées de l'assistant (plan §3.2, règles G1, G2 et G4).
 * Fonctions pures, appelées par le ConversationManager ; la même garde existe côté Api-Ai (IA-1.6).
 */

import { buildCountMessage } from "../../utils/countMessage";
import type { ViewFilter } from "../customView";
import { MailFolder } from "../dataManager/graph/types/graph";
import type { Mail } from "../mail";
import { MASS_ACTION_MAX } from "./constants";
import { ContentAction } from "./types/conversation";

// #region Messages

/** G1 : suppression demandée sans aucun critère. */
export const DELETE_WITHOUT_CRITERIA_MESSAGE =
    "Je ne peux pas supprimer sans savoir quels mails : précisez un expéditeur, un sujet ou une période.";

/** G4 : dossier corbeille pas encore chargé, impossible de savoir quels mails y sont déjà. */
export const TRASH_UNKNOWN_MESSAGE =
    "Je ne peux pas encore vérifier si ces mails sont déjà dans la corbeille : réessayez dans un instant.";

/** G2 : liste trop grande pour une action groupée (toujours au-delà du seuil, donc au pluriel). */
export const massActionMessage = (count: number): string =>
    `J'ai trouvé ${count} mails, c'est trop pour une action groupée : précisez.`;

/** G4 : mails retirés d'une suppression parce qu'ils sont déjà dans la corbeille. */
export const trashExcludedMessage = (count: number): string =>
    buildCountMessage(count, {
        empty: "",
        singular: "1 mail déjà dans la corbeille n'est pas concerné.",
        plural: "{count} mails déjà dans la corbeille ne sont pas concernés.",
    });

/** G4 : tous les mails de la liste sont déjà dans la corbeille. */
export const allInTrashMessage = (count: number): string =>
    buildCountMessage(count, {
        empty: "Aucun mail à supprimer.",
        singular:
            "Aucun mail à supprimer : le mail de la liste est déjà dans la corbeille.",
        plural: "Aucun mail à supprimer : les {count} mails de la liste sont déjà dans la corbeille.",
    });

// #endregion

// #region G1 — critères d'une recherche

const hasText = (values: string[] | undefined): boolean =>
    (values ?? []).some((value) => value.trim() !== "");

/**
 * G1 : le filtre porte-t-il au moins un critère ? Mots-clés, dates, expéditeurs ou destinataires,
 * catégories, statuts, lu ou non lu, pièce jointe. `limit` et `sortOrder` ne comptent pas.
 */
export function hasCriteria(filter: ViewFilter): boolean {
    return (
        hasText(filter.keywords) ||
        !!filter.startDate ||
        !!filter.endDate ||
        hasText(filter.identifiedSenders) ||
        hasText(filter.identifiedRecipients) ||
        hasText(filter.categories) ||
        hasText(filter.mailStatus) ||
        !!filter.readStatus ||
        filter.hasAttachment !== undefined
    );
}

// #endregion

// #region G2 — plafond d'action groupée

/**
 * G2 (F-1.3) : au-delà du seuil de masse (`MASS_ACTION_MAX`), pas d'action groupée (toute action
 * autre que la consultation) — la liste reste en consultation (`contentAction`), avec `massComment`
 * pour demander de préciser. L'appelant garde son texte lu.
 */
export function groupActionGuard<A extends ContentAction | undefined>(
    count: number,
    action: A,
): {
    isMassAction: boolean;
    contentAction: A | ContentAction.VIEW;
    massComment: string | undefined;
} {
    const isGroupAction = action !== undefined && action !== ContentAction.VIEW;
    const isMassAction = isGroupAction && count > MASS_ACTION_MAX;
    return {
        isMassAction,
        contentAction: isMassAction ? ContentAction.VIEW : action,
        massComment: isMassAction ? massActionMessage(count) : undefined,
    };
}

// #endregion

// #region G4 — corbeille

/**
 * Un mail est dans la corbeille si son dossier est celui de la corbeille : identifiant Graph chargé
 * par le MailManager, ou nom du dossier (`deleteditems`), que portent les mails Google
 * (`GoogleManager._extractParentFolder`) et une copie déplacée avant le chargement de l'identifiant.
 */
const isInTrash = (mail: Mail, trashFolderId: string): boolean =>
    mail.parentFolderId === trashFolderId ||
    mail.parentFolderId === MailFolder.Deleteditems;

/**
 * G4 : retire d'une suppression les mails déjà dans la corbeille, où elle les purgerait pour de bon.
 * Appelée à un seul endroit : `ConversationManager.applyTrashGuard`, sur la réponse d'un outil v1 ou
 * sur l'action proposée en fin de tour par étapes.
 */
export function excludeTrashForDelete(
    mails: Mail[],
    trashFolderId: string,
): { kept: Mail[]; excludedCount: number } {
    const kept = mails.filter((mail) => !isInTrash(mail, trashFolderId));
    return { kept, excludedCount: mails.length - kept.length };
}

// #endregion
